import styled from '@emotion/styled';
import { Button, Table } from 'antd';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import TileButton from 'components/atoms/TileButton';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import Legend from 'components/molecules/Legend';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import PortFolioAnaDriver from 'components/organism/PortFolioAnaDriver';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { portFolioExportData } from 'parser/graphToExcelDownloadParser';
import {
    getPortfolioAnalysisTableColumns,
    getPortfolioAnalysisTableColumnsV2,
} from 'utils/getPortfolioAnalysisColumns';
import {
    calculateContainerHeight,
    getAdditionalDataOnCopyClick,
    getConfig,
    reverseObjectKeyValues,
    parseAttributeType,
} from 'utils/helperMethods';
import { portfolioAnalysisTableData, portfolioAnalysisTableHeaderData } from 'utils/testData';
import { useFetchPortfolioBubbleChart } from 'hooks/portfolioAnalysis.tsx/useFetchPortfolioBubbleChart';
import { useFetchMechanicsTacticsData } from 'hooks/portfolioAnalysis.tsx/useFetchMechanicsTacticsData';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PORTFOLIO_ANALYSIS_SELECT_TAB_CLICKED,
    PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION,
    PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION_EXPORT,
} from 'utils/mixpanelEventNames';
import ROIMediaSpends from 'components/organism/PortfolioAnalysis.tsx/ROIMediaSpends';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import KPISelectorV2 from 'components/molecules/KPISelectorV2';
import { ProjectOutlined } from '@ant-design/icons';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { PortfolioUrls } from 'config/urls';
import { useFetchPortfolioAnalysisDataV2 } from 'hooks/portfolioAnalysis.tsx/useFetchPortfolioAnalysisDataV2';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import TableErrorBox from 'components/molecules/ErrorBox/TableErrorBox';
export const commonClassName = 'border  border-borderLinePurpuleAlt p-4 rounded-lg';
export const commonHeaderClass = 'font-medium font-xl m-0 text-headerColor';

export const legendData = [
    { label: 'Positive', color: colorConstants.lightGreen },
    { label: 'Neutral', color: colorConstants.lightGrey },
    { label: 'Negative', color: colorConstants.lightRed },
];

const portfolioAnalysisHeader = 'Portfolio Analysis';
const CustomTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        height: 44px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 100px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }

    // border: ${'1px solid #DBDFE9'};
    // table {
    //     border-collapse: separate;
    //     border-spacing: 0 15px;
    // }
`;

const portfolioAnalysisTableId = 'promo-library-table-id';

function setDropdownFilters(filters: any) {
    if (!filters)
        return {
            promotion_mechanic: [],
            tactic_description: [],
        };
    const tableHeaderData = {
        promotion_mechanic: [
            {
                key: 'select all',
                label: 'Select All',
                selected: true,
            },
            ...filters?.Mechanics,
        ],
        tactic_description: [
            {
                key: 'select all',
                label: 'Select All',
                selected: true,
            },
            ...filters?.Tactic,
        ],
    };
    return tableHeaderData;
}

function PortfolioAnalysis() {
    const { appConfig: config } = useContext(UserContext);
    const portfolioAnalysis = config.portfolioAnalysis;
    const { absTileData } = portfolioAnalysis;
    const defaultGroupByIndex = portfolioAnalysis.defaultGroupByIndex;
    const defaultGroupBy2Index = portfolioAnalysis.defaultGroupBy2Index;
    const chartDropdownExtraKpis = portfolioAnalysis.chartDropdownExtraKpis;
    const groupByHeaders = portfolioAnalysis?.groupByHeaders;
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
        filterNullError,
        allCheckedAttribute,
        timeSelectionLabel,
    } = useContext(FilterContextData);

    const [absTile, setAbsTile] = useState(absTileData[0]);

    const tableHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: portfolioAnalysisTableId,
        height: tableHeight.current,
        buffer: 80, //footer
    });

    //list with value
    const [selectedGroupByLevelDropdown, setSelectedGroupByLevelDropdown] = useState<any[]>(
        groupByHeaders?.filter((item: any) => item.isSelected).map((item: any) => item.value) ?? [
            groupByHeaders?.[0]?.value,
        ],
    );
    const [kpiDropdown, setKpiDropdown] = useState<any[]>([]);
    const [isKpiDropdownVisible, setIsKpiDropdownVisible] = useState(false);

    const enabledVariable =
        allDimAndAttributeTypeList &&
        'loading' in allDimAndAttributeTypeList &&
        !allDimAndAttributeTypeList['loading'] &&
        allDimAndAttributeTypeList['dimentions'] &&
        !idForRestOfComponentLoading &&
        !timePeriodLoading;

    //kpi config data
    const {
        loading: kpiLoading,
        error: kpiError,
        data: kpiHeaders,
    } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(enabledVariable) },
        url: PortfolioUrls.roiAnalysisKpiDataUrl,
        queryKey: ['roi-analysis-kpi-data'],
        variables: {},
        parserFunction: (res: any) => {
            if (res?.error || !res?.length) {
                throw 'No Data';
            }
            const result = res?.map((item: any) => {
                return {
                    label: item?.name,
                    value: item?.kpi_id,
                    pre: item?.pre_unit ?? '',
                    post: item?.post_unit ?? '',
                    isSelected: true,
                    sortKey: String(item?.kpi_id),
                };
            });
            setKpiDropdown(result);
            return {};
        },
    });

    //list with entire object
    const selectedGroupByLevelDropdownItems = useMemo(() => {
        return groupByHeaders?.filter((item: any) => {
            return selectedGroupByLevelDropdown.includes(item?.value);
        });
    }, [selectedGroupByLevelDropdown]);

    const tableRef = useRef(null);

    const {
        fetchPortfolioBubbleChart,
        data: bubbleData,
        kpiListData,
        loading: bubbleDataLoading,
    } = useFetchPortfolioBubbleChart();

    const {
        fetchMechanicsTacticsDropDownValues,
        error: mechanicsTacticsDropdownError,
        data: mechanicsTacticsDropdownData,
        loading: mechanicsTacticsDropdownLoading,
    } = useFetchMechanicsTacticsData();

    const [filterStates, setFilterStates] = useState<any>(null);
    const [groupBy1, setGroupBy1] = useState<any>('');
    const [groupBy2, setGroupBy2] = useState<any>('');
    const [xAxisVal, setXAxisval] = useState(kpiListData?.[3]);
    const [yAxis, setYAxis] = useState(kpiListData?.[2]);
    const [bubbleSize, setBubbleSize] = useState(kpiListData?.[1]);
    const [showChartLabel, setShowChartLabel] = useState(true);
    const [groupByOptions, setGroupByOptions] = useState<any[]>([]);
    const [portfolioAnalysisVariable, setPortfolioAnalysisVariable] = useState<any>({});
    const [portfolioAnalysisExportVariable, setPortfolioAnalysisExportVariable] = useState<any>({});

    // const {
    //     fetchPortfolioAnalysisData,
    //     data,
    //     tableHeaders,
    //     loading,
    //     count,
    //     setData: setPortfolioAnalysisData,
    //     error: portfolioAnalysisDataError,
    //     setLoading,
    // } = useFetchPortfolioAnalysis();

    //portfolio analysis data
    const {
        loading: portfolioAnalysisDataLoading,
        error: portfolioAnalysisDataError,
        data: portfolioAnalysisData,
        count: portfolioAnalysisDataCount,
        onFetchError: onFetchPortfolioAnalysisDataError,
    } = useFetchPortfolioAnalysisDataV2({
        method: 'post',
        queryConfig: { enabled: Boolean(portfolioAnalysisVariable?.field_filters) },
        url: PortfolioUrls.roiAnalysisTableDataUrl,
        queryKey: ['roi-analysis-table-data', portfolioAnalysisVariable],
        variables: { ...(portfolioAnalysisVariable ?? {}) },
        kpiConfig: kpiDropdown,
    });

    //table data export api
    const {
        loading: portfolioAnalysisExportDataLoading,
        error: portfolioAnalysisExportDataError,
        data: portfolioAnalysisExportData,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(portfolioAnalysisExportVariable?.field_filters) },
        url: PortfolioUrls.roiAnalysisTableDataExportUrl,
        queryKey: ['roi-analysis-table-data-export', portfolioAnalysisExportVariable],
        variables: { ...(portfolioAnalysisExportVariable ?? {}) },
        parserFunction: (res: any) => {
            if (Array.isArray(res?.header)) {
                selectedGroupByLevelDropdownItems?.forEach((item: any) => {
                    // Find index of the item to replace
                    const index = res?.header?.indexOf(item?.value);

                    // If found, replace the value with label
                    if (index !== -1) {
                        res.header[index] = item?.label;
                    }
                });
            }

            return res;
        },
    });

    //set portfolio analysis table variable
    useEffect(() => {
        if (kpiDropdown?.length && filterStates) {
            // scroll infinite scroller to top
            const infScrollerElem = document
                .getElementById('portfolio-analysis-infinite-scroller')
                ?.getElementsByClassName('infinite-scroll-component')[0];
            if (infScrollerElem) infScrollerElem.scrollTo(0, 0);

            //taking only selected keys
            const fieldFilters: any = {};

            Object.entries(filterStates).forEach(([filter, items]) => {
                const selectedKeys = (items as any)
                    .filter((item: any) => item.selected && item.key !== 'select all')
                    .map((item: any) => item.key);
                if (selectedKeys.length > 0) {
                    fieldFilters[filter] = selectedKeys;
                }
            });
            const portfolioAnalysisTempVariable = {
                limit: portfolioAnalysis.limit,
                offset: 0,
                start_timestamp: JSON.parse(timePeriodSelection ?? '{}')?.start_timestamp,
                end_timestamp: JSON.parse(timePeriodSelection ?? '{}')?.end_timestamp,
                field_filters: fieldFilters,
                sort_by: selectedGroupByLevelDropdownItems?.[0]?.value,
                sort_order: 'asc',
                filters: idForRestOfComponent,
                kpis_list: kpiDropdown?.filter((item: any) => item.isSelected).map((kpi: any) => kpi?.value),
                selected_fields_list: selectedGroupByLevelDropdownItems
                    ?.filter((item: any) => {
                        return !item.dimension;
                    })
                    .map((groupByItem: any) => groupByItem?.value),
                dim_fields_list: selectedGroupByLevelDropdownItems
                    ?.filter((item: any) => {
                        return item.dimension;
                    })
                    .map((groupByItem: any) => {
                        return {
                            name: groupByItem?.value,
                            dimension: groupByItem?.dimension,
                        };
                    }),
            };
            setPortfolioAnalysisVariable({ ...portfolioAnalysisTempVariable });
            //export variables
            delete portfolioAnalysisTempVariable.limit;
            setPortfolioAnalysisExportVariable({ ...portfolioAnalysisTempVariable });
        }
    }, [kpiDropdown, filterStates, selectedGroupByLevelDropdownItems]);

    useEffect(() => {
        if (enabledVariable && groupBy1) {
            // let sneakGroupBy1 = _.snakeCase((groupBy1.label || '').toLowerCase());
            // let sneakGroupBy2 = _.snakeCase((groupBy2.label || '').toLowerCase());
            fetchPortfolioBubbleChart({
                variables: {
                    timePeriod: String(timePeriodSelection).toLowerCase(),
                    groupBy1: groupBy1?.dataKey,
                    dimension1: groupBy1?.dimension,
                    groupBy2: groupBy2?.dataKey,
                    dimension2: groupBy2?.dimension,
                    filters: JSON.stringify(idForRestOfComponent),
                },
            });
        }

        //setting driver groupby options
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !groupBy1
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions']);
            if (data.length) {
                data = data.map((item: any) => ({ ...item, dataKey: _.snakeCase((item?.label || '').toLowerCase()) }));
                const lastDataItem = data?.[data?.length - 1];
                let other = chartDropdownExtraKpis?.map((item: any, index: number) => {
                    return {
                        ...item,
                        value: lastDataItem?.value + index + 1,
                    };
                });
                let addedOther = [...data, ...other];
                setGroupByOptions(addedOther);
                setGroupBy1(defaultGroupByIndex >=addedOther.length ? addedOther[0] : addedOther[defaultGroupByIndex]);
                setGroupBy2(
                    defaultGroupBy2Index >= addedOther.length ? addedOther[0] : addedOther[defaultGroupBy2Index],
                );
            }
        }
    }, [
        idForRestOfComponent,
        filterStates,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
    ]);

    useEffect(() => {
        //cleanup function
        return () => {
            setGroupByOptions([]);
            setGroupBy1('');
            setGroupBy2('');
        };
    }, [allDimAndAttributeTypeList?.loading]);

    useEffect(() => {
        // setFilterStates(setDropdownFilters(portfolioAnalysis));
        if (enabledVariable) {
            fetchMechanicsTacticsDropDownValues({
                variables: {
                    timePeriod: String(timePeriodSelection).toLowerCase(),
                    filters: JSON.stringify(idForRestOfComponent),
                },
            });
        }
    }, [idForRestOfComponent, idForRestOfComponentLoading, allDimAndAttributeTypeList?.loading, timePeriodSelection]);

    useEffect(() => {
        if (!mechanicsTacticsDropdownLoading && mechanicsTacticsDropdownData?.Tactic) {
            setFilterStates(setDropdownFilters(mechanicsTacticsDropdownData));
        }
    }, [mechanicsTacticsDropdownData]);

    useEffect(() => {
        if (mechanicsTacticsDropdownError) {
            setFilterStates(null);
            onFetchPortfolioAnalysisDataError(mechanicsTacticsDropdownError);
            setPortfolioAnalysisVariable({});
            setPortfolioAnalysisExportVariable({});
        }
    }, [mechanicsTacticsDropdownError]);

    const { loading: commonLoading } = useDebounceLoading({
        loadingDependency:
            idForRestOfComponentLoading ||
            allDimAndAttributeTypeList?.loading ||
            portfolioAnalysisDataLoading ||
            mechanicsTacticsDropdownLoading,
    });

    const memoisedTableColumns = useMemo(() => {
        if (!portfolioAnalysisData) return [];
        const mergedColumns = [
            ...(selectedGroupByLevelDropdownItems ?? []),
            ...(kpiDropdown?.filter((item) => item.isSelected) ?? []),
        ];

        return getPortfolioAnalysisTableColumnsV2(mergedColumns, filterStates, setFilterStates, {
            sortBy: portfolioAnalysisVariable?.sort_by,
            sortOrder: portfolioAnalysisVariable?.sort_order === 'asc' ? 'ascend' : 'descend',
        });
    }, [portfolioAnalysisData, filterStates]);

    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            const newOffset = portfolioAnalysisVariable.offset + portfolioAnalysisVariable.limit;
            setPortfolioAnalysisVariable({ ...portfolioAnalysisVariable, offset: newOffset });
        }
    };

    const onGroupByDropdownChange = (groupBy1: any, groupBy2: any) => {
        setGroupBy1(groupBy1);
        setGroupBy2(groupBy2);
        //setLoading(true);
        // let sneakGroupBy1 = _.snakeCase(groupBy1.label.toLowerCase());
        // let sneakGroupBy2 = _.snakeCase(groupBy2.label.toLowerCase());
        fetchPortfolioBubbleChart({
            variables: {
                timePeriod: String(timePeriodSelection).toLowerCase(),
                groupBy1: groupBy1.dataKey,
                dimension1: groupBy1.dimension,
                groupBy2: groupBy2.dataKey,
                dimension2: groupBy2.dimension,
                filters: JSON.stringify(idForRestOfComponent),
            },
        });
    };
    useEffect(() => {
        if (kpiListData?.length) {
            if (!yAxis) setYAxis(kpiListData?.[2]);
            if (!xAxisVal) setXAxisval(kpiListData?.[3]);
            if (!bubbleSize) setBubbleSize(kpiListData?.[1]);
        }
    }, [kpiListData]);

    return (
        <section className={` ${commonClassName} bg-white`}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div ref={tableRef}>
                    <div className="flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <h2 className={commonHeaderClass}>{portfolioAnalysis?.mainHeader}</h2>
                            <TileButton
                                disabled={commonLoading}
                                data={absTileData}
                                activeKey={absTile.key}
                                onClick={(item: any) => {
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_SELECT_TAB_CLICKED, {
                                        tabName: item.label,
                                    });
                                    setAbsTile(item);
                                }}
                                style={{ marginRight: 16 }}
                            />
                        </div>
                    </div>
                    {/* Levels Dropdown */}
                    <div className="flex gap-2 my-2 justify-between items-center">
                        {[absTileData[0].key].includes(absTile.key) && (
                            <div className="flex items-center gap-2">
                                <p className="font-xs ">Levels </p>
                                <SelectionDropDown
                                    className="dropdown-width-multi-select uniqSelPTWeeks"
                                    list={groupByHeaders}
                                    checkedListObject={''}
                                    disabled={false}
                                    selectAll={false}
                                    setSelectedList={setSelectedGroupByLevelDropdown}
                                    selectedList={selectedGroupByLevelDropdown}
                                />
                            </div>
                        )}

                        {[absTileData[0].key].includes(absTile.key) && (
                            <div className="flex items-center gap-4">
                                <Legend data={legendData} />
                                <KPISelectorV2
                                    buttonComponent={
                                        <Button
                                            onClick={() => {
                                                setIsKpiDropdownVisible(!isKpiDropdownVisible);
                                            }}
                                            icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                            style={{ borderRadius: 4, margin: 0 }}
                                            type="default"
                                        >
                                            KPI
                                        </Button>
                                    }
                                    isVisible={isKpiDropdownVisible}
                                    setIsVisible={setIsKpiDropdownVisible}
                                    kpiList={kpiDropdown}
                                    variableName="KPI"
                                    hideSearch
                                    onSubmit={(items) => {
                                        const temp = kpiDropdown?.map((item: any) => {
                                            return {
                                                ...item,
                                                isSelected: items.includes(item?.value),
                                            };
                                        });
                                        setKpiDropdown(temp);
                                    }}
                                />
                                <CopyToClipBoard
                                    mixpanelEvent={() => trackEventMixpanel(PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION)}
                                    refOfComponent={tableRef}
                                />
                                {!portfolioAnalysisExportDataLoading &&
                                    portfolioAnalysisData?.length > 0 &&
                                    portfolioAnalysisExportData?.data?.length > 0 && (
                                        <DownloadExcel
                                            mixpanelEvent={() =>
                                                trackEventMixpanel(PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION_EXPORT, {
                                                    Table_Name: 'Portfolio Analysis Table Data',
                                                })
                                            }
                                            title={'Portfolio Analysis Table Data'}
                                            excelData={portfolioAnalysisExportData?.data ?? []}
                                            headers={portfolioAnalysisExportData?.header ?? []}
                                        />
                                    )}
                            </div>
                        )}
                    </div>
                    {/* PROMOTION */}
                    {absTile.key === absTileData[0].key && (
                        <div className="mt-4" id="portfolio-analysis-infinite-scroller">
                            <InfiniteScroller
                                data={portfolioAnalysisData || []}
                                loading={commonLoading}
                                hasMore={portfolioAnalysisData?.length < portfolioAnalysisDataCount || commonLoading}
                                height={
                                    portfolioAnalysisData && !portfolioAnalysisDataError
                                        ? tableHeight.current ?? undefined
                                        : isNaN(Number(tableHeight.current))
                                        ? undefined
                                        : Number(tableHeight.current) - 1
                                }
                                //height={200}
                                //     loader={true}
                                onNext={() => infiniteScrollOnNext(commonLoading)}
                            >
                                <CustomTable
                                    style={{
                                        opacity: commonLoading ? '0.3' : 1,
                                        pointerEvents: commonLoading ? 'none' : 'auto',
                                    }}
                                    showSorterTooltip={false}
                                    pagination={false}
                                    columns={memoisedTableColumns}
                                    dataSource={portfolioAnalysisData}
                                    scroll={{ x: '100%' }}
                                    sticky={{
                                        getContainer: () =>
                                            document.getElementsByClassName(
                                                'infinite-scroll-component',
                                            )?.[0] as HTMLElement,
                                    }}
                                    onChange={(pagination, filter, sort: any) => {
                                        //if fixed is not present, it means its coming from kpi column
                                        if (sort?.order) {
                                            const sortMapping: any = {
                                                descend: 'desc',
                                                ascend: 'asc',
                                            };
                                            setPortfolioAnalysisVariable({
                                                ...portfolioAnalysisVariable,
                                                offset: 0,
                                                sort_by: sort?.column?.sortKey,
                                                sort_order: sortMapping?.[sort?.order] || 'asc',
                                            });

                                            //default it to dim1 column on cancelling sorting
                                        } else {
                                            setPortfolioAnalysisVariable({
                                                ...portfolioAnalysisVariable,
                                                offset: 0,
                                                sort_by: selectedGroupByLevelDropdownItems?.[0]?.value,
                                                sort_order: 'asc',
                                            });
                                        }
                                    }}
                                    id={portfolioAnalysisTableId}
                                    locale={{
                                        emptyText: commonLoading ? (
                                            <></>
                                        ) : portfolioAnalysisDataError ? (
                                            <TableErrorBox
                                                error={portfolioAnalysisDataError}
                                                height={tableHeight.current}
                                            />
                                        ) : (
                                            ''
                                        ),
                                    }}
                                />
                            </InfiniteScroller>
                        </div>
                    )}

                    {/* DRIVERS */}
                    {absTile.key === absTileData[1].key && (
                        <PortFolioAnaDriver
                            xAxisVal={xAxisVal}
                            setXAxisval={setXAxisval}
                            bubbleSize={bubbleSize}
                            setBubbleSize={setBubbleSize}
                            yAxis={yAxis}
                            setYAxis={setYAxis}
                            showChartLabel={showChartLabel}
                            setShowChartLabel={setShowChartLabel}
                            error={portfolioAnalysisDataError}
                            chartData={bubbleData}
                            kpiListData={kpiListData}
                            onGroupByDropdownChange={onGroupByDropdownChange}
                            groupBy2={groupBy2}
                            groupBy1={groupBy1}
                            groupByOptions={groupByOptions}
                            loading={bubbleDataLoading}
                            allCheckedAttribute={allCheckedAttribute}
                            timeSelectionLabel={timeSelectionLabel}
                        />
                    )}
                    {/* MEDIA */}
                    {absTile.key === absTileData[2]?.key && <ROIMediaSpends tableRef={tableRef} />}
                </div>
            )}
        </section>
    );
}

export default PortfolioAnalysis;
